import { useEffect, useState } from 'react';
import axios from 'axios';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClipboardList, Download, Loader2, RefreshCw } from 'lucide-react';

function formatWhen(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('fail') || s === 'rejected') return 'border-cp-crit text-cp-crit';
  if (s.includes('succeed') || s === 'done' || s === 'approved') return 'border-cp-ok text-cp-ok';
  if (s.includes('start') || s === 'pending') return 'border-cp-mod text-cp-mod';
  return 'border-cp-line text-cp-muted';
}

function eventLabel(event) {
  const details = event?.details || {};
  if (details.summary) return details.summary;
  if (event?.summary) return event.summary;
  if (details.to_stage) {
    const from = details.from_stage ? `${details.from_stage} → ` : '';
    return `${from}${details.to_stage}`;
  }
  return event?.action || 'event';
}

function eventStatus(event) {
  const details = event?.details || {};
  // Use the event's own status only. Snapshot approval_status on early
  // stage rows stays "pending" forever and reads as if the ticket is still
  // waiting after the flow is DONE.
  if (details.status) return details.status;
  if (event?.status) return event.status;
  return '';
}

function eventStep(event) {
  const details = event?.details || {};
  return details.step || event?.step || event?.action || '';
}

/**
 * NUP / workflow audit package — stage transitions + agent MCP actions from Postgres.
 */
export default function NupAuditPackageCard({ ticketNumber, embeddedTrail = [], refreshKey = '' }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [packageData, setPackageData] = useState(null);

  const load = async () => {
    if (!ticketNumber) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `${INCIDENTS_API}/tickets/${encodeURIComponent(ticketNumber)}/audit-package`
      );
      setPackageData(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load audit package');
      setPackageData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ticketNumber) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(
          `${INCIDENTS_API}/tickets/${encodeURIComponent(ticketNumber)}/audit-package`
        );
        if (!cancelled) setPackageData(data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || err?.message || 'Failed to load audit package');
          setPackageData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketNumber, refreshKey]);

  const events = Array.isArray(packageData?.events) ? packageData.events : [];
  const trail =
    Array.isArray(packageData?.audit_trail) && packageData.audit_trail.length
      ? packageData.audit_trail
      : Array.isArray(embeddedTrail)
        ? embeddedTrail
        : [];

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(packageData || { ticket_number: ticketNumber, events, audit_trail: trail }, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ticketNumber || 'nup'}-audit-package.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card data-testid="nup-audit-package">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Audit Package
          </CardTitle>
          <CardDescription>
            Agent actions and workflow decisions persisted for this request
          </CardDescription>
        </div>
        <div className="flex items-center gap-1.5">
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => void load()} title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={downloadJson}
            disabled={!packageData && !trail.length && !events.length}
            title="Download JSON"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {packageData && (
          <div className="flex flex-wrap gap-2 text-xs">
            {packageData.workflow_stage && (
              <Badge variant="outline" className="border-cp-line">
                Stage: {packageData.workflow_stage}
              </Badge>
            )}
            {packageData.approval?.status && (
              <Badge variant="outline" className={statusBadgeClass(packageData.approval.status)}>
                Approval: {packageData.approval.status}
                {packageData.approval.by ? ` · ${packageData.approval.by}` : ''}
              </Badge>
            )}
            {packageData.extracted?.new_user && (
              <Badge variant="outline" className="border-cp-line font-mono">
                User: {packageData.extracted.new_user}
              </Badge>
            )}
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading audit package…
          </div>
        )}

        {!loading && error && (
          <p className="text-sm text-cp-crit">{error}</p>
        )}

        {!loading && !error && events.length === 0 && trail.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No audit events yet. Agent steps will appear here as the workflow runs.
          </p>
        )}

        {!loading && events.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Persisted events ({events.length})
            </p>
            <ol className="space-y-2 max-h-[min(420px,50vh)] overflow-y-auto pr-1">
              {events.map((event) => (
                <li
                  key={event.id || `${event.action}-${event.timestamp}`}
                  className="rounded-lg border border-cp-line bg-cp-inset/40 px-3 py-2 text-sm"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-cp-muted">{eventStep(event)}</span>
                    {eventStatus(event) && (
                      <Badge variant="outline" className={`text-[10px] ${statusBadgeClass(eventStatus(event))}`}>
                        {eventStatus(event)}
                      </Badge>
                    )}
                    <span className="ml-auto text-[11px] text-muted-foreground">
                      {formatWhen(event.timestamp)}
                    </span>
                  </div>
                  <p className="mt-1 text-cp-fg">{eventLabel(event)}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Actor: {event.actor || '—'}
                    {event.details?.tool ? ` · Tool: ${event.details.tool}` : ''}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        )}

        {!loading && trail.length > 0 && events.length === 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              In-state trail ({trail.length})
            </p>
            <ol className="space-y-2 max-h-[min(320px,40vh)] overflow-y-auto">
              {trail.map((event, idx) => (
                <li key={`${event.at}-${idx}`} className="rounded-lg border border-cp-line px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs">{event.step}</span>
                    <Badge variant="outline" className={`text-[10px] ${statusBadgeClass(event.status)}`}>
                      {event.status}
                    </Badge>
                    <span className="ml-auto text-[11px] text-muted-foreground">{formatWhen(event.at)}</span>
                  </div>
                  <p className="mt-1">{event.summary}</p>
                </li>
              ))}
            </ol>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
